import { useEffect, useRef } from "react";

const isDocumentHidden = () =>
  typeof document !== "undefined" && document.visibilityState === "hidden";

/**
 * Calls `callback` every `intervalMs` while the page is visible.
 *
 * Polling pauses while the page is hidden (backgrounded tab, locked phone,
 * app switched away), so the device's radio and CPU can idle. When the page
 * becomes visible again, a poll that came due while hidden runs immediately
 * and the interval resumes. The hook does not run on mount; callers keep
 * their own initial load.
 *
 * @param {() => void} callback  latest callback is always used
 * @param {number} intervalMs
 * @param {{ enabled?: boolean }} [options]
 */
export function usePolling(callback, intervalMs, { enabled = true } = {}) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled || !(intervalMs > 0)) return undefined;

    let timer = null;
    let lastRun = Date.now();

    const tick = () => {
      lastRun = Date.now();
      callbackRef.current();
    };
    const start = () => {
      if (timer === null) timer = setInterval(tick, intervalMs);
    };
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibilityChange = () => {
      if (isDocumentHidden()) {
        stop();
        return;
      }
      if (Date.now() - lastRun >= intervalMs) tick();
      start();
    };

    if (!isDocumentHidden()) start();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled, intervalMs]);
}
